export type BlogParagraph = { type: "p"; text: string };
export type BlogHeading = { type: "h"; text: string };
export type BlogBlock = BlogParagraph | BlogHeading;

export type BlogPost = {
  slug: string;
  title: string;
  subtitle: string;
  category: string;
  date: string; // human readable
  isoDate: string;
  readMins: number;
  cover: string;
  publisher: string;
  publisherNote: string;
  excerpt: string;
  body: BlogBlock[];
};

export const posts: BlogPost[] = [
  {
    slug: "from-waste-to-power",
    title: "From Waste to Power: Building Energy Self-Reliance",
    subtitle: "India's Next Freedom Movement May Be About Energy",
    category: "Energy & Innovation",
    date: "May 12, 2026",
    isoDate: "2026-05-12",
    readMins: 4,
    cover: "/img/blog-researchlab.jpg",
    publisher: "MY Bharat — Ministry of Youth Affairs & Sports, Govt. of India",
    publisherNote: "Originally published on the MY Bharat Voices portal.",
    excerpt:
      "Nearly 88% of India's crude oil is imported. As the country races toward batteries and EVs, a quiet question remains: can a nation be truly free if the technology powering its future is still imported?",
    body: [
      {
        type: "p",
        text: "For decades, India has fought an invisible battle, energy dependence. Today, nearly 88% of India's crude oil demand is met through imports, costing the nation more than $100 billion annually. Every geopolitical conflict in the Middle East, every disruption in global shipping routes, and every spike in crude prices directly impacts India's economy and the lives of 1.4 billion people.",
      },
      {
        type: "p",
        text: "India understood this vulnerability early. That is why the country aggressively accelerated toward renewable energy, electric mobility, and battery-powered infrastructure. But in this transition emerged a dangerous irony.",
      },
      {
        type: "p",
        text: "We were replacing dependence on imported oil with dependence on imported batteries.",
      },
      {
        type: "p",
        text: "This creates an important question for India: Can a country truly become energy independent if the core technologies powering its future are still imported?",
      },
      { type: "h", text: "The Global Battery Trap" },
      {
        type: "p",
        text: "Today's battery ecosystem is dominated by foreign supply chains dependent on critical minerals, imported technologies, and concentrated manufacturing hubs. While these systems enabled rapid EV growth globally, they also create long-term strategic vulnerabilities for countries like India.",
      },
      {
        type: "p",
        text: "Meanwhile, millions of Indian homes, telecom towers, e-rickshaws, and rural systems still rely on lead-acid batteries — bulky, inefficient, short-lived, and environmentally hazardous.",
      },
      {
        type: "p",
        text: "India's future cannot depend entirely on imported ecosystems or outdated technologies.",
      },
      {
        type: "h",
        text: "The Rise of India's Indigenous Energy Revolution: From “Make in India” to “Invent in India”",
      },
      {
        type: "p",
        text: "Across India, young researchers, engineers, and innovators are working toward something much bigger than business growth. They are working toward technological sovereignty.",
      },
      {
        type: "p",
        text: "DEVANANTA R&D PRIVATE LIMITED is among the Indian startups focused on developing indigenous next-generation energy storage technologies using advanced nano-materials and sustainable approaches.",
      },
      {
        type: "p",
        text: "The vision is simple but powerful: Build advanced energy technologies in India, for the world, using Indian innovation. Instead of depending entirely on imported ecosystems, the goal is to create sustainable, high-performance energy storage systems that are efficient, scalable and safer.",
      },
      {
        type: "p",
        text: "One of the powerful aspects of this mission is the idea of converting waste into advanced materials. India generates enormous amounts of industrial byproducts. Much of this waste is either discarded or burned, contributing to pollution and environmental damage. By transforming waste-derived materials into advanced battery components, Indian startups are not only addressing energy challenges but also contributing to a circular and sustainable economy.",
      },
      {
        type: "p",
        text: "This is not just innovation. It is nation-building through science and technology.",
      },
      { type: "h", text: "Why Energy Storage Will Define the Future?" },
      {
        type: "p",
        text: "Most people think batteries are only important for electric vehicles. But in reality, energy storage is becoming the backbone of modern civilisation.",
      },
      {
        type: "p",
        text: "The future of transportation, healthcare, defence, logistics, renewable energy, disaster management, drones, AI infrastructure, and even agriculture will depend on reliable and efficient energy systems.",
      },
      {
        type: "p",
        text: "Whoever controls advanced energy storage technologies will help shape the future global economy. And this is where India has an opportunity to lead.",
      },
      {
        type: "p",
        text: "Indigenous energy storage technologies can transform every sector of India, from powering soldiers in Siachen and enabling longer-range drones to making EVs more affordable and strengthening rural infrastructure. Reliable, low-cost batteries can support village micro-grids, solar irrigation, cold-storage systems, and even create new income opportunities by converting agricultural waste into valuable battery materials.",
      },
      {
        type: "p",
        text: "The most painful reminder of energy dependence came from a tragic incident in Uttar Pradesh in 2025. A young kidney patient undergoing dialysis reportedly died after a power failure interrupted treatment because the backup diesel generator ran out of fuel. That story is heartbreaking; it also reveals the real importance of energy storage. Reliable batteries are not merely technological products. They are uninterrupted hospitals, life-saving medical systems, vaccine cold chains, today's rural-healthcare needs, emergency-response infrastructure, and human dignity itself.",
      },
      { type: "h", text: "A Defining Moment for India" },
      {
        type: "p",
        text: "Every generation witnesses a defining transition. For previous generations, it was industrialisation and information technology. For this generation, it may be energy technology.",
      },
      {
        type: "p",
        text: "India today stands at the edge of one of the biggest industrial transformations in modern history. The world is moving toward electrification, renewable energy, intelligent infrastructure, and AI-driven systems.",
      },
      {
        type: "p",
        text: "But the nations that will lead this transition will not simply be the ones with oil reserves or mineral wealth. They will be the nations that innovate.",
      },
      {
        type: "p",
        text: "Indian startups, researchers, and young innovators are increasingly proving that India can move beyond being a consumer of global technology and become a creator of globally relevant solutions.",
      },
      {
        type: "p",
        text: "The journey toward energy self-reliance will not happen overnight. It will require research, manufacturing, policy support, infrastructure, and long-term commitment. But every indigenous innovation brings India one step closer to technological independence.",
      },
      {
        type: "p",
        text: "Today's Indian startup ecosystem represents a larger national movement, one where science, sustainability, innovation, and patriotism come together to build solutions for the future.",
      },
      {
        type: "p",
        text: "The future of India's energy story should not be written through dependence. It should be written through Indian innovation, Indian science, and Global Impact.",
      },
    ],
  },
];

export const getPost = (slug: string): BlogPost | undefined =>
  posts.find((p) => p.slug === slug);
